/** @jsxImportSource @emotion/react */
import { useAtomValue } from 'jotai'
import { Checkbox, Link, List, ListItem } from '@mui/material'
import { useTheme } from '@mui/material/styles'
import { useMemo, useState, lazy } from 'react'
import { Box, FormControlLabel, Typography } from '@mui/material'

import { FEATURE_FLAGS } from '@app/app-constants'
import { useFeatureFlags } from '@app/hooks/useFeatureFlags'
import { clientConfigAtom, clientLogoAtom } from '@app/state/clientsAtom'

import { Modal } from '@app/components/Atoms/Modal'
import { Button } from '@app/components/Atoms/Button'
const UiMarkdown = lazy(() => import('@app/components/Atoms/UiMarkdown/UiMarkdown'))

import {
  logo,
  footer,
  uiLogo,
  header,
  tsncsCopy,
  tsncsModal,
  checkboxLabel,
  continueButton,
  secondLinkItem,
  tsncsContainer,
  headerContainer,
  footerImageWrapper,
  acceptanceControls,
  tsncsInnerContainer,
  acceptanceControlsInner,
  acceptanceControlsSecondCheckbox,
} from './TsnCsModal.styles'
import { checkboxMainClass } from '@app/styles/global.styles'

export interface TsnCsModalProps {
  content: string
  handleAcceptTsnCs: () => void
  showClientLogo?: boolean
}
/**
 * A modal for terms and conditions acceptance
 */
export const TsnCsModal = ({
  handleAcceptTsnCs,
  content,
  showClientLogo = false,
}: TsnCsModalProps) => {
  const theme = useTheme()
  const [accepted, setAccepted] = useState(false)
  const [secondAccepted, setSecondAccepted] = useState(false)

  const { useFeatureFlag } = useFeatureFlags()
  const logos = useAtomValue(clientLogoAtom)
  const clientConfig = useAtomValue(clientConfigAtom)
  const secondTsncsCheckboxEnabled = useFeatureFlag(FEATURE_FLAGS.SECOND_TSNCS_CHECKBOX)
  const secondTsncsLinks = useMemo(() => {
    if (!secondTsncsCheckboxEnabled) return null
    if (!clientConfig) return null
    if (Object.hasOwn(clientConfig, 'second_checkbox_links')) {
      return clientConfig.second_checkbox_links
    }
  }, [secondTsncsCheckboxEnabled, clientConfig])

  const handleContinue = () => {
    handleAcceptTsnCs()
  }

  return (
    <Modal open={true}>
      <Box css={tsncsModal({ theme })}>
        <Box css={headerContainer()}>
          <Typography variant="h4" css={header({ theme })}>
            Terms and Conditions
          </Typography>
        </Box>
        <Box css={tsncsContainer({ theme })}>
          <Box css={tsncsInnerContainer({ theme })}>
            <UiMarkdown css={tsncsCopy({ theme })}>{content}</UiMarkdown>
          </Box>
        </Box>
        <Box css={footer({ theme })}>
          <Box className="footer-image-group" css={footerImageWrapper}>
            {showClientLogo && (
              <Box key={logos?.full} component="img" src={logos?.full} css={logo({ theme })} />
            )}

            <Box component="img" src="/logos/UILogoFull.svg" css={uiLogo({ theme })} />
          </Box>

          <Box css={acceptanceControls}>
            {secondTsncsCheckboxEnabled && secondTsncsLinks ? (
              <Box className="second-tsncs-checkbox" css={acceptanceControlsSecondCheckbox}>
                <Typography variant="h6" css={checkboxLabel({ theme })}>
                  Welcome to the Resilience Explorer
                </Typography>
                <Typography css={checkboxLabel({ theme })}>
                  To ensure the Resilience Explorer is used consistently, responsibly, and as
                  intended, make sure you have reviewed the following resources:
                </Typography>
                <List>
                  {secondTsncsLinks.map((link, index) => {
                    return (
                      <ListItem key={link.label + index} css={secondLinkItem({ theme })}>
                        <Link href={link.link} underline="always" target="_blank" rel="noreferrer">
                          {link.label}
                        </Link>
                      </ListItem>
                    )
                  })}
                </List>
                <Typography css={checkboxLabel({ theme })}>
                  They explain appropriate use, limitations of the data, and good practice for
                  sharing insights.
                </Typography>
                <FormControlLabel
                  control={
                    <Checkbox
                      onChange={(_e, checked) => {
                        setSecondAccepted(checked)
                      }}
                      size="small"
                      css={checkboxMainClass}
                    />
                  }
                  label={
                    <Typography css={checkboxLabel({ theme })}>
                      I understand and agree to use the Resilience Explorer in line with these
                      documents.
                    </Typography>
                  }
                />
              </Box>
            ) : null}
            <Box css={acceptanceControlsInner}>
              <FormControlLabel
                control={
                  <Checkbox
                    onChange={(_e, checked) => {
                      setAccepted(checked)
                    }}
                    size="small"
                    css={checkboxMainClass}
                  />
                }
                label={
                  <Typography css={checkboxLabel({ theme })}>
                    I agree to the above Terms and Conditions
                  </Typography>
                }
              />
              <Button
                disabled={secondTsncsCheckboxEnabled ? !accepted || !secondAccepted : !accepted}
                onClick={handleContinue}
                css={continueButton({ theme })}
                data-testid="tsncs-continue-button"
              >
                Continue
              </Button>
            </Box>
          </Box>
        </Box>
      </Box>
    </Modal>
  )
}
